import type { CommunityMaterialPrice, SpotMaterialPrice } from './api';
import type { CatalogMaterial } from './materialCatalogGroups';
import { weightLabel, type WeightUnit } from '@/domains/paydirt/materialUnits';
import { formatUpdatedAgo } from './formatUpdatedAgo';

export function materialPriceSummary(
  m: CatalogMaterial,
  communityPrices: Record<string, CommunityMaterialPrice>,
  spotPrices: Record<string, SpotMaterialPrice>,
): string | null {
  if (m.valuationMethod === 'spot') {
    const spot = spotPrices[m.materialId];
    if (!spot) return 'Spot price loading…';
    const usd = Number(spot.pricePerUnitUsd);
    if (!Number.isFinite(usd) || usd <= 0) return 'Spot price unavailable';
    const updated = spot.fetchedAt ? ` · updated ${formatUpdatedAgo(spot.fetchedAt)}` : '';
    return `~$${usd.toFixed(2)}/${weightLabel(spot.valuationUnit as WeightUnit)}${updated}`;
  }

  const ref = communityPrices[m.materialId];
  if (ref?.pricingMode === 'piece_curve' && ref.points && ref.points.length > 0) {
    const min = [...ref.points].sort((a, b) => a.stoneCarats - b.stoneCarats)[0];
    const pieceUsd = Number(min.referencePiecePriceUsd);
    if (Number.isFinite(pieceUsd) && pieceUsd > 0) {
      return `from ~$${pieceUsd.toFixed(0)}/stone (${ref.rawSampleCount} samples)`;
    }
  }
  if (ref?.referencePriceUsd) {
    const gramUsd = Number(ref.referencePriceUsd);
    if (Number.isFinite(gramUsd) && gramUsd > 0) {
      return `~$${gramUsd.toFixed(2)}/g (${ref.rawSampleCount} samples)`;
    }
  }
  if (ref && ref.rawSampleCount > 0) {
    return `${ref.rawSampleCount} samples (insufficient for reference price)`;
  }
  if (m.valuationMethod === 'nominal') return null;
  return 'Insufficient community data';
}
